// Matrice de routage : entrées en lignes, sorties en colonnes. Verrouillée par défaut,
// comme un panneau de grille en régie : on déverrouille avant de commuter.
import { useState } from "react";
import { api, unwrap } from "../api/client";
import type { InputView, OutputView, StateMessage } from "../api/types";
import { HEALTH_TONE, OUTPUT_TONE } from "../components/status";
import { Button, cx, Dot, EmptyState, PageHeader, useToast } from "../components/ui";
import { useCan } from "../lib/auth";
import { inputHealth, OUTPUT_STATE_LABEL } from "../lib/format";
import { t } from "../lib/i18n";

export function Matrix({ state }: { state: StateMessage }) {
  const toast = useToast();
  const canRoute = useCan()("operator");
  const [unlocked, setUnlocked] = useState(false);
  const [pending, setPending] = useState<number | null>(null);
  const { inputs, outputs, alarms } = state;

  const route = async (output: OutputView, input: InputView) => {
    if (!unlocked || output.input_id === input.id) return;
    setPending(output.id);
    try {
      await unwrap(api.POST("/api/outputs/{output_id}/route", { params: { path: { output_id: output.id } }, body: { input_id: input.id } }));
      toast(`« ${output.name} » ← ${input.name}`);
    } catch (e) {
      toast((e as Error).message, "err");
    } finally {
      setPending(null);
    }
  };

  if (inputs.length === 0 || outputs.length === 0) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title={t("Matrice de routage")} />
        <EmptyState title={t("Matrice vide")}>{t("Il faut au moins une entrée et une sortie pour router.")}</EmptyState>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title={t("Matrice de routage")}
        subtitle={!canRoute ? t("Consultation : le routage est réservé aux opérateurs.")
          : unlocked ? t("Déverrouillée : un clic change la source d'une sortie (coupure de quelques secondes).")
          : t("Verrouillée : déverrouillez pour changer la source d'une sortie.")}
        actions={canRoute && <Button variant={unlocked ? "danger" : "secondary"} onClick={() => setUnlocked((u) => !u)}>
          {unlocked ? t("Verrouiller") : t("Déverrouiller")}
        </Button>} />

      <div className="flex flex-wrap gap-x-5 gap-y-1 text-[12px] text-muted">
        <span className="inline-flex items-center gap-1.5"><span className="size-3 rounded-sm bg-accent" /> {t("source de la sortie")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="grid size-3 place-items-center rounded-sm border border-dashed border-warn/70 font-cond text-[9px] text-warn">S</span> {t("secours")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="size-2.5 rounded-full bg-ok" /> {t("entrée diffusée en ce moment")}</span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-line bg-surface">
        <table className="border-collapse text-left">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-surface px-4 py-2 font-cond text-[12px] font-semibold uppercase tracking-wider text-faint">
                {t("Entrées ↓ · Sorties →")}
              </th>
              {outputs.map((o) => (
                <th key={o.id} className="min-w-28 border-l border-line px-3 py-2 align-bottom">
                  <div className="font-cond text-[14px] font-semibold leading-tight">{o.name}</div>
                  <div className="mt-1 flex items-center gap-1.5 text-[11px] text-muted">
                    <Dot tone={OUTPUT_TONE[o.status.state]} /> {t(OUTPUT_STATE_LABEL[o.status.state])}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {inputs.map((i) => (
              <tr key={i.id} className="border-t border-line">
                <th scope="row" className="sticky left-0 z-10 bg-surface px-4 py-2 text-left font-normal">
                  <div className="flex items-center gap-2">
                    <Dot tone={HEALTH_TONE[inputHealth(i, alarms)]} />
                    <span className="font-medium">{i.name}</span>
                  </div>
                  <div className="pl-4 font-mono text-[11px] text-faint">{i.id}</div>
                </th>
                {outputs.map((o) => {
                  const active = o.input_id === i.id;
                  const backup = o.backup_input_id === i.id;
                  const onAir = o.status.state !== "stopped" && o.status.active_input === i.id;
                  return (
                    <td key={o.id} className="border-l border-line p-1.5 text-center">
                      <button type="button" disabled={!unlocked || pending !== null}
                        onClick={() => route(o, i)}
                        title={backup ? t("Entrée de secours de cette sortie") : undefined}
                        aria-label={active ? t("Routée : {input} vers {output}", { input: i.name, output: o.name })
                          : backup ? t("Secours : {input} vers {output}", { input: i.name, output: o.name })
                          : t("Router : {input} vers {output}", { input: i.name, output: o.name })}
                        aria-pressed={active}
                        className={cx("relative size-9 rounded border transition-colors",
                          active ? "border-accent bg-accent" : backup ? "border-warn/70 border-dashed bg-bg" : "border-line bg-bg",
                          unlocked && !active && "cursor-pointer hover:border-accent hover:bg-accent/20",
                          pending === o.id && "animate-pulse")}>
                        {backup && <span className="font-cond text-[11px] font-semibold text-warn">S</span>}
                        {onAir && <span className="absolute -top-1 -right-1 size-2.5 rounded-full border-2 border-surface bg-ok" />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
